import React, { useCallback, useEffect, useMemo, useRef, memo } from 'react';
import { useSelector } from 'react-redux';
import { FormikProvider, useFormik } from 'formik';
import { EnvironmentOutlined, ScheduleOutlined } from '@ant-design/icons';
import onewayTripIcon from '../../../assets/svgs/onewaytripicon.svg';
import roundTripIcon from '../../../assets/svgs/roundtripIcon.svg';
import { Input, Radio, Switch } from 'antd';

import { stepperInfo } from '../../forms/CreateRide/stepperInfo';
import { useStepThreeHandlers } from '../../forms/CreateRide/StepThree/useStepThreeHandlers';
import { useDateTimeHandlers } from '../../forms/CreateRide/StepThree/useDateTimeHandlers';
import { PickUpDateTimeSection } from '../../forms/CreateRide/StepThree/PickUpDateTimeSection';
import { ReturnDateTimeSection } from '../../forms/CreateRide/StepThree/ReturnDateTimeSection';
import { RecurringSection } from '../../forms/CreateRide/StepThree/RecurringSection';
import { ThirdStepTooltip } from '../../forms/CreateRide/StepThree/ThirdStepTooltip';
import {
  DATE_TIME_FIELD_LABELS,
  DATE_TIME_LABEL_VARIANT,
} from '../../forms/CreateRide/StepThree/stepThreeDateTimeConstants';
import { useCheckForValidWindowOfTime } from '../../../hooks/useCheckForValidWindowOfTime';
import { useCheckForMatchedTc } from '../../../hooks/useCheckForMatchedTc';
import { useUberHealthServiceAvailability } from '../../../hooks/useUberHealthServiceAvailability';
import { GooglePlacesAutoComplete } from '../../formControls/GooglePlacesAutoComplete/GooglePlacesAutoComplete';
import { CustomCheckbox } from '../../formControls/Checkbox/Checkbox';
import GoogleDistanceMatrix from '../../common/GoogleMaps/DistanceMatrix';
import { Snackbar } from '../../common/Snackbar/Snackbar';
import {
  DateHelper,
  LodashHelper,
  transformDraftToRide,
} from '../../../utils';
import { isAddressInvalid } from '../../../utils/helpers';
import {
  ADDITIONAL_DELAY_FOR_RIDE_CREATION,
  INVALID_ADDRESS_MESSAGE,
  MINIMUM_DELAY_FOR_ROUNDTRIP_RIDE,
  UBER_SERVICE_UNAVAILABLE_MESSAGE,
  VEHICLE_TYPES,
} from '../../../constants/constants';
import { defaultTimeValues } from '../../forms/Preferences/MHPreferences/partials/defaultValues';
import { isDriverRemainOnSiteSchedulingWindow } from '../../../utils/RideHelper';
import styles from './quickScheduleNew.module.css';

const { TextArea } = Input;

const MAX_ADDRESS_DISPLAY_LENGTH = 120;

const truncateAddressValue = (value) => {
  if (!value || typeof value !== 'string') return value;
  return value.length > MAX_ADDRESS_DISPLAY_LENGTH
    ? value.slice(0, MAX_ADDRESS_DISPLAY_LENGTH)
    : value;
};

const FieldLabel = ({ children, required }) => (
  <label className={`${styles.fieldLabel} ${required ? styles.required : ''}`}>
    {children}
  </label>
);

function getThirdStepInitialValues(draft, defaultTimeZone) {
  const base = {
    ...stepperInfo[2].values,
    ...draft?.step3,
    pickUpDate: draft?.step3?.pickUpDate || { timezone: defaultTimeZone },
  };

  if (draft?.step3?.isRecurring && draft?.step3?.isRoundtrip) {
    return {
      ...base,
      returnTime: draft?.step3?.returnTime || { timezone: defaultTimeZone },
      recurrenceRule: {
        ...base?.recurrenceRule,
        startPoint:
          draft?.step3?.recurrenceRule?.startPoint ||
          draft?.step3?.pickUpDate || { timezone: defaultTimeZone },
        returnStartPoint:
          draft?.step3?.recurrenceRule?.returnStartPoint ||
          draft?.step3?.returnTime || { timezone: defaultTimeZone },
      },
    };
  }

  if (draft?.step3?.isRecurring) {
    return {
      ...base,
      recurrenceRule: {
        ...base?.recurrenceRule,
        startPoint:
          draft?.step3?.recurrenceRule?.startPoint ||
          draft?.step3?.pickUpDate || {
            datetime: draft?.step3?.pickUpDate?.datetime || '',
            timezone: defaultTimeZone,
          },
      },
    };
  }

  if (draft?.step3?.isRoundtrip) {
    return {
      ...base,
      returnTime: draft?.step3?.returnTime || { timezone: defaultTimeZone },
    };
  }

  return base;
}

const DRIVER_REMAIN_ON_SITE_LABEL =
  'Is driver required to remain on-site between Leg A & B';

const DriverRemainOnSiteRadioRow = ({ value, onChange, disabled }) => (
  <div className={`${styles.toggleRow} ${styles.driverRemainOnSiteRow}`}>
    <span className={styles.driverRemainOnSiteLabel}>{DRIVER_REMAIN_ON_SITE_LABEL}</span>
    <Radio.Group
      className={styles.driverRemainOnSiteRadioGroup}
      value={value ?? false}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
    >
      <Radio value={true}>Yes</Radio>
      <Radio value={false}>No</Radio>
    </Radio.Group>
  </div>
);

const ToggleRow = ({
  text,
  checked,
  onChange,
  disabled,
  showInfo,
  tooltipRange,
  useFlexibleHoursMessage,
  emphasized,
}) => (
  <div className={`${styles.toggleRow} ${emphasized ? styles.toggleRowEmphasized : ''}`}>
    <div className={styles.toggleRowLeft}>
      <span className={`${styles.toggleRowText} ${emphasized ? styles.toggleRowTextEmphasized : ''}`}>
        {text}
        {showInfo && tooltipRange && (
          <span className={styles.toggleInfoWrap}>
            <ThirdStepTooltip
              range={tooltipRange}
              useFlexibleHoursMessage={useFlexibleHoursMessage}
              compact
            />
          </span>
        )}
      </span>
    </div>
    <Switch checked={Boolean(checked)} onChange={onChange} disabled={disabled} />
  </div>
);

const ScheduleRidePanel = ({
  draft,
  stepOneValues,
  stepTwoValues,
  platformPreferences,
  defaultTimeZone,
  airportBufferTime,
  onValuesChange,
  onValidChange,
}) => {
  const hasMatchedTC = useSelector((state) => state.rides.hasMatchedTC);
  const uberHealthRouteAvailable = useSelector((state) => state.rides.uberHealthRouteAvailable);
  const uberHealthRouteCheckPending = useSelector((state) => state.rides.uberHealthRouteCheckPending);
  const windowOfTimeIsValid = useSelector((state) => state.rides.windowOfTimeIsValid);

  const stepThreeDraft = useMemo(() => {
    if (draft?.step3?.flightInfo && draft?.step3?.airportBufferTime && draft?.step3?.pickUpDate?.datetime) {
      const subtractedDate = DateHelper.subtractMinutesInDateWithoutFormat(
        draft.step3.pickUpDate.datetime,
        draft.step3.airportBufferTime,
      );
      return {
        ...draft.step3,
        pickUpDate: { ...draft.step3.pickUpDate, datetime: subtractedDate },
      };
    }
    return draft?.step3;
  }, [draft?.step3]);

  const initialValues = useMemo(
    () => getThirdStepInitialValues(draft, defaultTimeZone),
    [draft, defaultTimeZone],
  );

  const formik = useFormik({
    enableReinitialize: true,
    validateOnMount: true,
    validateOnChange: true,
    validateOnBlur: true,
    initialValues,
    validationSchema: stepperInfo[2].schema(true),
  });

  const { values, touched, errors, validateForm, setFieldValue, setFieldTouched, setValues, isValid } =
    formik;

  const isUberRide = stepTwoValues?.vehicleType === VEHICLE_TYPES.UBER_AMBULATORY_RIDE;
  const defaultHomeAddress = stepOneValues?.riderInfo?.homeAddress;

  const minDelayToStartRide = useMemo(() => {
    const vehicleType = stepTwoValues?.vehicleType;
    if (isUberRide) {
      return { hours: 0, minutes: 30 };
    }
    const pref = platformPreferences?.minTimeFromRideRequestToPickup?.[vehicleType];
    if (!pref) {
      return { hours: 0, minutes: 0 };
    }
    return DateHelper.addHours(
      pref,
      ADDITIONAL_DELAY_FOR_RIDE_CREATION,
      stepTwoValues?.needsCarSeat
        ? platformPreferences?.carBoosterSeatBufferTime
        : defaultTimeValues,
    );
  }, [isUberRide, platformPreferences, stepTwoValues?.vehicleType, stepTwoValues?.needsCarSeat]);

  const {
    pickUpAddress,
    destinationAddress,
    handleSelectAddress,
    handleChangeAddress: handleChangeAddressBase,
    handleAddressBookSelectAddress,
    handleUseDefaultHomeAddress,
  } = useStepThreeHandlers({
    defaultHomeAddress,
    validateForm,
    setFieldValue,
  });

  const handleChangeAddress = useCallback(
    (fieldName) =>
      async (payload) => {
        const value = truncateAddressValue(payload?.value);
        return handleChangeAddressBase(fieldName)({ ...payload, value });
      },
    [handleChangeAddressBase],
  );

  const handleSelectAddressWrapped = useCallback(
    (fieldName) =>
      async (payload) => {
        if (payload?.value?.formatted_address) {
          const truncated = truncateAddressValue(payload.value.formatted_address);
          if (truncated !== payload.value.formatted_address) {
            payload = {
              ...payload,
              value: { ...payload.value, formatted_address: truncated },
            };
          }
        }
        return handleSelectAddress(fieldName)(payload);
      },
    [handleSelectAddress],
  );

  const dateTimeHandlers = useDateTimeHandlers({
    validateForm,
    setFieldTouched,
    defaultValues: stepThreeDraft,
    minDelayToStartRide,
    maxPickUpWindowOfTime: platformPreferences?.maxPickUpWindowOfTime,
    minimumRoundTrip: platformPreferences?.minimumRoundTrip || MINIMUM_DELAY_FOR_ROUNDTRIP_RIDE,
    isUberRide,
  });

  const {
    handlePickUpDateTime,
    handleReturnTime,
    handleStartPointTime,
    handleReturnStartPoint,
    handleRoundTripCheckbox,
    disabledDatesForPickUpDate,
    disabledDatesForAppointment,
    disabledDatesForReturnTime,
    disabledDatesForStartPointOfRecurring,
    disabledDatesForReturnTimeForRoundTripRecurring,
    disabledDateTime,
    handleChangeTZ,
    pickUpDate,
    returnTimeDate,
    startPointOfRecurring,
    returnStartPoint,
    setStartPointOfRecurring,
    setReturnStartPoint,
    handleReccuringCheckbox,
    handleUseWindowOfTime,
    disabledDateForHigherLimit,
    disabledTimeForHigherLimit,
    handleChangePickUpDateToLowerLimit,
    pickUpDateLowerLimit,
    returnTimeLowerLimit,
    handleReturnTimeLowerLimit,
    disabledDateForReturnHigherLimit,
    disabledDateTimeForReturnHigherLimit,
    handleChangeappointmentDate,
    appointmentDate,
    handleAppointmentDateCheckBox,
  } = dateTimeHandlers;

  useCheckForValidWindowOfTime({
    defaultValues: stepThreeDraft,
    currentValues: values,
    vehicleType: stepTwoValues?.vehicleType,
  });

  const valuesForMatching = useMemo(() => {
    if (!stepOneValues || !stepTwoValues) return null;
    return transformDraftToRide({
      step1: stepOneValues,
      step2: stepTwoValues,
      step3: values,
    });
  }, [stepOneValues, stepTwoValues, values]);

  useCheckForMatchedTc(valuesForMatching, Boolean(valuesForMatching) && !isUberRide);
  useUberHealthServiceAvailability(valuesForMatching, Boolean(valuesForMatching) && isUberRide);

  const showOnSiteDriverWait = useMemo(
    () =>
      Boolean(
        values?.isRoundtrip &&
          !values?.isRecurring &&
          !isUberRide &&
          isDriverRemainOnSiteSchedulingWindow(values?.pickUpDate, values?.returnTime),
      ),
    [values?.isRoundtrip, values?.isRecurring, values?.pickUpDate, values?.returnTime, isUberRide],
  );

  useEffect(() => {
    if (!showOnSiteDriverWait && values?.driverRemainOnSite) {
      setFieldValue('driverRemainOnSite', false);
    }
  }, [showOnSiteDriverWait, values?.driverRemainOnSite, setFieldValue]);

  const isUberRoundTrip = isUberRide && values?.isRoundtrip;

  useEffect(() => {
    if (!isUberRoundTrip) return;
    if (values?.isPickUpDateFlexible) {
      void setFieldValue('isPickUpDateFlexible', false);
      void setFieldValue('pickUpDateLowerLimit', undefined);
    }
  }, [isUberRoundTrip, values?.isPickUpDateFlexible, setFieldValue]);

  useEffect(() => {
    validateForm();
  }, [pickUpAddress, destinationAddress, validateForm]);

  // Match legacy StepThree quickSchdule: confirm uses formik validity only (not TC/time-window gates).
  const stepThreeValid = useMemo(() => {
    if (isUberRide) {
      return isValid && uberHealthRouteAvailable && !uberHealthRouteCheckPending;
    }
    return isValid;
  }, [
    isValid,
    isUberRide,
    uberHealthRouteAvailable,
    uberHealthRouteCheckPending,
  ]);

  const lastEmittedRef = useRef({ values: null, valid: null });

  useEffect(() => {
    if (lastEmittedRef.current.values === values && lastEmittedRef.current.valid === stepThreeValid) {
      return;
    }
    lastEmittedRef.current = { values, valid: stepThreeValid };
    onValuesChange?.(values);
    onValidChange?.(stepThreeValid);
  }, [values, stepThreeValid, onValuesChange, onValidChange]);

  const handleChildValueChange = useCallback(
    async (fieldName, updatedValue) => {
      await setFieldValue(fieldName, updatedValue);
    },
    [setFieldValue],
  );

  const handleTripTypeChange = useCallback(
    (isRound) => {
      handleRoundTripCheckbox({
        name: 'isRoundtrip',
        checked: isRound,
        values,
        setValues,
      });
      if (isRound && isUberRide) {
        void setFieldValue('isPickUpDateFlexible', false);
        void setFieldValue('pickUpDateLowerLimit', undefined);
      }
    },
    [handleRoundTripCheckbox, values, setValues, isUberRide, setFieldValue],
  );

  const handlePickupFlexibleChange = useCallback(
    (checked) => {
      handleUseWindowOfTime({
        name: 'isPickUpDateFlexible',
        values,
        checked,
        setFieldValue,
      });
    },
    [handleUseWindowOfTime, values, setFieldValue],
  );

  const handleReturnFlexibleChange = useCallback(
    (checked) => {
      handleUseWindowOfTime({
        name: 'isReturnDateFlexible',
        values,
        checked,
        setFieldValue,
      });
    },
    [handleUseWindowOfTime, values, setFieldValue],
  );

  const handleRecurringToggle = useCallback(
    (checked) => {
      handleReccuringCheckbox({
        name: 'isRecurring',
        checked,
        values,
        setFieldValue,
      });
    },
    [handleReccuringCheckbox, values, setFieldValue],
  );

  const updatedPickupDateTime = useMemo(() => {
    if (values?.flightInfo && pickUpDate && airportBufferTime) {
      return DateHelper.addMinutesInDateWithFormat(pickUpDate, airportBufferTime);
    }
    return null;
  }, [pickUpDate, values?.flightInfo, airportBufferTime]);

  const minimumRoundTrip =
    platformPreferences?.minimumRoundTrip || MINIMUM_DELAY_FOR_ROUNDTRIP_RIDE;

  const pickUpDateTimeSectionProps = {
    handleUseWindowOfTime,
    handlePickUpDateTime,
    handleChangeTZ,
    pickUpDate,
    disabledDatesForPickUpDate,
    disabledDatesForAppointment,
    disabledDateTime,
    setFieldValue,
    values,
    windowOfTimeIsValid,
    minDelayToStartRide,
    maxPickUpWindowOfTime: platformPreferences?.maxPickUpWindowOfTime,
    disabledDateForHigherLimit,
    disabledTimeForHigherLimit,
    handleChangePickUpDateToLowerLimit,
    pickUpDateLowerLimit,
    handleChangeappointmentDate,
    appointmentDate,
    handleAppointmentDateCheckBox,
    showTitle: false,
    isUberRide,
    hideFlexibleCheckbox: true,
    hideAppointmentCheckbox: true,
    labelVariant: DATE_TIME_LABEL_VARIANT.SCHEDULE_RIDE,
    fieldInputClassName: styles.inputFilled,
    selectFieldClassName: `${styles.inputFilled} ${styles.selectField}`,
  };

  const returnDateTimeSectionProps = {
    pickUpDate,
    appointmentDate,
    returnTimeDate,
    maxPickUpWindowOfTime: platformPreferences?.maxPickUpWindowOfTime,
    handleReturnTime,
    disabledDatesForReturnTime,
    disabledDateTime,
    handleUseWindowOfTime,
    handleChangeTZ,
    values,
    minimumRoundTrip,
    returnTimeLowerLimit,
    handleReturnTimeLowerLimit,
    disabledDateForReturnHigherLimit,
    disabledDateTimeForReturnHigherLimit,
    isUberRide,
    hideFlexibleCheckbox: true,
    labelVariant: DATE_TIME_LABEL_VARIANT.SCHEDULE_RIDE,
    fieldInputClassName: styles.inputFilled,
    selectFieldClassName: `${styles.inputFilled} ${styles.selectField}`,
  };

  const tripTypeTabs = (
    <div className={styles.tripTypeTabs}>
      <button
        type="button"
        className={`${styles.tripTypeTab} ${!values?.isRoundtrip ? styles.tripTypeTabActive : ''}`}
        onClick={() => handleTripTypeChange(false)}
      >
        <img src={onewayTripIcon} alt="" className={styles.tripTypeTabSvgIcon} aria-hidden />
        One-Way Trip
      </button>
      <button
        type="button"
        className={`${styles.tripTypeTab} ${values?.isRoundtrip ? styles.tripTypeTabActive : ''}`}
        onClick={() => handleTripTypeChange(true)}
      >
        <img src={roundTripIcon} alt="" className={styles.tripTypeTabSvgIcon} aria-hidden />
        <span>Round Trip</span>
        <span className={styles.tripTypeTabTooltip}>
          <ThirdStepTooltip range={minimumRoundTrip} useFlexibleHoursMessage={false} compact />
        </span>
      </button>
    </div>
  );

  const renderAddressField = (fieldKey, label, placeholder, addressValue, addressObjKey) => (
    <div className={styles.formRowFull}>
      <FieldLabel required>{label}</FieldLabel>
      <div className={styles.scheduleRideAddressField}>
        <div className={styles.scheduleRideAddressInputShell}>
          <GooglePlacesAutoComplete
            name={`${addressObjKey}.formattedAddress`}
            handleSelectAddress={handleSelectAddressWrapped(addressObjKey)}
            handleAddressBookSelectAddress={handleAddressBookSelectAddress(addressObjKey)}
            handleChangeAddress={handleChangeAddress(addressObjKey)}
            placeholder={placeholder}
            value={addressValue}
            myclassname={`${styles.inputFilled} ${styles.scheduleRideGoogleInput}`}
            showFavoriteIcon={false}
            currentAddress={values?.[addressObjKey]?.formattedAddress}
            currentAddressObj={values?.[addressObjKey]}
          />
          <div className={styles.scheduleRideAddressIcons} aria-hidden>
            <EnvironmentOutlined className={styles.scheduleRideLocationIcon} />
          </div>
        </div>
      </div>
      {addressObjKey === 'pickupAddressDetails' &&
        touched?.pickupAddressDetails?.formattedAddress &&
        isAddressInvalid(errors?.pickupAddressDetails) && (
          <div className={styles.fieldError} role="alert">
            {INVALID_ADDRESS_MESSAGE}
          </div>
        )}
      {addressObjKey === 'destinationAddress' && (
        <div className={styles.fieldError} role="alert">
          {LodashHelper.isString(errors.destinationAddress)
            ? errors.destinationAddress
            : touched?.destinationAddress?.formattedAddress &&
              errors?.destinationAddress &&
              isAddressInvalid(errors?.destinationAddress) &&
              INVALID_ADDRESS_MESSAGE}
        </div>
      )}
      {addressObjKey === 'pickupAddressDetails' &&
        values?.destinationAddress?.formattedAddress &&
        values?.pickupAddressDetails?.formattedAddress && (
          <GoogleDistanceMatrix
            destination={values.destinationAddress.formattedAddress}
            origin={values.pickupAddressDetails.formattedAddress}
            handleChildValueChange={handleChildValueChange}
          />
        )}
      {defaultHomeAddress && (
        <label className={styles.useDefaultAddress}>
          <CustomCheckbox
            name={
              addressObjKey === 'pickupAddressDetails'
                ? 'useDefaultPickUpAddress'
                : 'useDefaultDestinationAddress'
            }
            onChange={handleUseDefaultHomeAddress(addressObjKey)}
            checked={LodashHelper.isEqual(values?.[addressObjKey], defaultHomeAddress)}
          />
          <span>Apply Home Address from Rider&apos;s Profile</span>
        </label>
      )}
    </div>
  );

  return (
    <FormikProvider value={formik}>
      {renderAddressField(
        'pickup',
        'Pickup Address',
        'Where from?',
        pickUpAddress,
        'pickupAddressDetails',
      )}

      {renderAddressField(
        'destination',
        'Drop-off Address',
        'Where to?',
        destinationAddress,
        'destinationAddress',
      )}

      {!hasMatchedTC && !isUberRide && (
        <div className={styles.scheduleRideAlert}>
          <Snackbar
            type="error"
            title="No Suitable Vehicles"
            content="There are no suitable vehicles in this area. Please, change the Pick up/Destination addresses or the vehicle type in the previous step"
          />
        </div>
      )}

      {isUberRide && !uberHealthRouteAvailable && !uberHealthRouteCheckPending && (
        <div className={styles.scheduleRideAlert}>
          <Snackbar type="error" title="Uber Unavailable" content={UBER_SERVICE_UNAVAILABLE_MESSAGE} />
        </div>
      )}

      {tripTypeTabs}

      {!values?.isRoundtrip ? (
        <>
          <ToggleRow
            text={DATE_TIME_FIELD_LABELS.FLEXIBLE_PICKUP_CHECKBOX}
            checked={values?.isPickUpDateFlexible}
            onChange={handlePickupFlexibleChange}
            showInfo
            tooltipRange={platformPreferences?.maxPickUpWindowOfTime}
            useFlexibleHoursMessage
            emphasized
          />
          <div
            className={`${styles.scheduleRideDatetimeEmbed} ${
              values?.isPickUpDateFlexible
                ? styles.scheduleRideDatetimeFlexible
                : styles.scheduleRideDatetimeFixed
            }`}
          >
            <PickUpDateTimeSection {...pickUpDateTimeSectionProps} />
          </div>
        </>
      ) : (
        <>
          <div className={styles.legSection}>
            <h4 className={styles.legTitle}>Leg A</h4>
            {!isUberRoundTrip && (
              <ToggleRow
                text={DATE_TIME_FIELD_LABELS.FLEXIBLE_PICKUP_CHECKBOX}
                checked={values?.isPickUpDateFlexible}
                onChange={handlePickupFlexibleChange}
                showInfo
                tooltipRange={platformPreferences?.maxPickUpWindowOfTime}
                useFlexibleHoursMessage
                emphasized
              />
            )}
            <div
              className={`${styles.scheduleRideDatetimeEmbed} ${
                !isUberRoundTrip && values?.isPickUpDateFlexible
                  ? styles.scheduleRideDatetimeFlexible
                  : styles.scheduleRideDatetimeFixed
              }`}
            >
              <PickUpDateTimeSection
                {...pickUpDateTimeSectionProps}
                labelVariant={DATE_TIME_LABEL_VARIANT.ROUND_TRIP}
              />
            </div>
          </div>

          <div className={styles.legSection}>
            <h4 className={styles.legTitle}>Leg B</h4>
            <ToggleRow
              text={DATE_TIME_FIELD_LABELS.FLEXIBLE_PICKUP_CHECKBOX}
              checked={values?.isReturnDateFlexible}
              onChange={handleReturnFlexibleChange}
              showInfo
              tooltipRange={platformPreferences?.maxPickUpWindowOfTime}
              useFlexibleHoursMessage
              emphasized
            />
            <div
              className={`${styles.scheduleRideDatetimeEmbed} ${
                values?.isReturnDateFlexible
                  ? styles.scheduleRideDatetimeFlexible
                  : styles.scheduleRideDatetimeFixed
              }`}
            >
              <ReturnDateTimeSection
                {...returnDateTimeSectionProps}
                labelVariant={DATE_TIME_LABEL_VARIANT.ROUND_TRIP}
              />
            </div>
          </div>

          {showOnSiteDriverWait && (
            <DriverRemainOnSiteRadioRow
              value={values?.driverRemainOnSite}
              onChange={(selected) => setFieldValue('driverRemainOnSite', selected)}
            />
          )}
        </>
      )}

      {values?.flightInfo && updatedPickupDateTime ? (
        <p style={{ color: 'red' }}>
          {`Note: Your ride is scheduled for ${updatedPickupDateTime} allowing time for baggage collection and airport formalities.`}
        </p>
      ) : null}

      {!windowOfTimeIsValid && (
        <div className={styles.scheduleRideAlert}>
          <Snackbar
            type="warning"
            title={DATE_TIME_FIELD_LABELS.INVALID_PICKUP_WINDOW_TITLE}
            content={DATE_TIME_FIELD_LABELS.INVALID_PICKUP_WINDOW_CONTENT}
          />
        </div>
      )}

      <div className={styles.divider} />

      {!isUberRide && (
        <div className={styles.recurringBlock}>
          <div className={styles.recurringText}>
            <h4>
              Set as Recurring Ride
            </h4>
            <p>Repeat this ride automatically based on your selected schedule.</p>
          </div>
          <Switch
            className={styles.recurringSwitch}
            checked={Boolean(values?.isRecurring)}
            onChange={handleRecurringToggle}
            disabled={values?.isPickUpDateFlexible || values?.isReturnDateFlexible}
          />
        </div>
      )}

      {values?.isRecurring && (
        <div className={styles.scheduleRideRecurringEmbed}>
          <RecurringSection
            values={values}
            validateForm={validateForm}
            pickUpDate={pickUpDate}
            startPointOfRecurring={startPointOfRecurring}
            setStartPointOfRecurring={setStartPointOfRecurring}
            setReturnStartPoint={setReturnStartPoint}
            handleChangeTZ={handleChangeTZ}
            returnStartPoint={returnStartPoint}
            handleStartPointTime={handleStartPointTime}
            handleReturnStartPoint={handleReturnStartPoint}
            disabledDatesForStartPointOfRecurring={disabledDatesForStartPointOfRecurring}
            minimumRoundTrip={minimumRoundTrip}
            disabledDatesForReturnTimeForRoundTripRecurring={
              disabledDatesForReturnTimeForRoundTripRecurring
            }
            disabledDateTime={disabledDateTime}
            minDelayToStartRide={minDelayToStartRide}
            labelVariant={DATE_TIME_LABEL_VARIANT.SCHEDULE_RIDE}
            fieldInputClassName={styles.inputFilled}
            selectFieldClassName={`${styles.inputFilled} ${styles.selectField}`}
          />
        </div>
      )}
    </FormikProvider>
  );
};

export default memo(ScheduleRidePanel);
